// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  Search, 
  Filter, 
  Users, 
  TrendingUp, 
  DollarSign, 
  Clock,
  Star,
  Award,
  Target,
  CheckCircle
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import AffiliatePipeline from '@/components/sales/AffiliatePipeline';
import AffiliateProspectForm from '@/components/sales/AffiliateProspectForm';
import AffiliateApplicationReview from '@/components/sales/AffiliateApplicationReview';
import AffiliatePerformance from '@/components/sales/AffiliatePerformance';

interface AffiliateProspect {
  id: string;
  sales_rep_id: string;
  name: string;
  email: string;
  company_name?: string;
  website_url?: string;
  social_media_handles?: any;
  audience_size?: number;
  niche_focus?: string;
  lead_source: string;
  pipeline_stage: string;
  notes?: string;
  contact_date?: string;
  next_follow_up?: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

interface AffiliateApplication {
  id: string;
  prospect_id: string;
  affiliate_profile_id?: string;
  application_data: any;
  marketing_channels?: string[];
  experience_level?: string;
  expected_monthly_referrals?: number;
  commission_tier: string;
  status: string;
  reviewed_by?: string;
  review_notes?: string;
  approved_at?: string;
  rejected_at?: string;
  created_at: string;
  updated_at: string;
}

interface AffiliateStats {
  totalProspects: number;
  pendingApplications: number;
  activeAffiliates: number;
  monthlyPayouts: number;
  conversionRate: number;
  totalRevenue: number;
}

const AffiliatesPage: React.FC = () => {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  
  // Data states
  const [prospects, setProspects] = useState<AffiliateProspect[]>([]);
  const [applications, setApplications] = useState<AffiliateApplication[]>([]);
  const [stats, setStats] = useState<AffiliateStats>({
    totalProspects: 0,
    pendingApplications: 0,
    activeAffiliates: 0,
    monthlyPayouts: 0,
    conversionRate: 0,
    totalRevenue: 0
  });
  
  // Modal states
  const [prospectFormOpen, setProspectFormOpen] = useState(false);
  const [editingProspect, setEditingProspect] = useState<AffiliateProspect | null>(null);
  const [reviewApplicationOpen, setReviewApplicationOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<AffiliateApplication | null>(null);

  useEffect(() => {
    if (profile?.id) {
      loadAffiliateData();
    }
  }, [profile?.id]);

  const loadAffiliateData = async () => {
    try {
      setLoading(true);

      // Load affiliate prospects
      const { data: prospectsData, error: prospectsError } = await supabase
        .from('affiliate_prospects')
        .select('*')
        .eq('sales_rep_id', profile?.id)
        .order('created_at', { ascending: false });

      if (prospectsError) throw prospectsError;

      // Load affiliate applications
      const { data: applicationsData, error: applicationsError } = await supabase
        .from('affiliate_applications')
        .select(`
          *,
          affiliate_prospects (name, email, company_name)
        `)
        .order('created_at', { ascending: false });

      if (applicationsError) throw applicationsError;

      // Calculate stats
      const totalProspects = prospectsData?.length || 0;
      const pendingApplications = applicationsData?.filter(app => app.status === 'pending').length || 0;
      const activeAffiliates = applicationsData?.filter(app => app.status === 'approved').length || 0;
      
      setProspects(prospectsData || []);
      setApplications(applicationsData || []);
      setStats({
        totalProspects,
        pendingApplications,
        activeAffiliates,
        monthlyPayouts: 0, // This would come from actual payout data
        conversionRate: totalProspects > 0 ? (activeAffiliates / totalProspects) * 100 : 0,
        totalRevenue: 0 // This would come from performance data
      });
    } catch (error) {
      console.error('Error loading affiliate data:', error);
      toast.error('Failed to load affiliate data');
    } finally {
      setLoading(false);
    }
  };

  const handleProspectSave = (savedProspect: AffiliateProspect) => {
    if (editingProspect?.id) {
      setProspects(prev => prev.map(p => p.id === savedProspect.id ? savedProspect : p));
    } else {
      setProspects(prev => [savedProspect, ...prev]);
    }
    setProspectFormOpen(false);
    setEditingProspect(null);
    loadAffiliateData(); // Refresh stats
  };

  const handleProspectEdit = (prospect: AffiliateProspect) => {
    setEditingProspect(prospect);
    setProspectFormOpen(true);
  };

  const handleApplicationReview = (application: AffiliateApplication) => {
    setSelectedApplication(application);
    setReviewApplicationOpen(true);
  };

  const handleApplicationUpdate = (updatedApplication: AffiliateApplication) => {
    setApplications(prev => prev.map(app => 
      app.id === updatedApplication.id ? updatedApplication : app
    ));
    setReviewApplicationOpen(false);
    setSelectedApplication(null);
    loadAffiliateData(); // Refresh stats
  };

  const getFilteredProspects = () => {
    return prospects.filter(prospect => {
      const matchesSearch = !searchTerm || 
        prospect.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (prospect.company_name && prospect.company_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        prospect.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStage = filterStage === 'all' || prospect.pipeline_stage === filterStage;
      const matchesSource = filterSource === 'all' || prospect.lead_source === filterSource;
      
      return matchesSearch && matchesStage && matchesSource;
    });
  };

  const getPendingApplications = () => {
    return applications.filter(app => app.status === 'pending' || app.status === 'under_review');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Affiliate Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage affiliate recruitment, applications, and performance
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={() => setProspectFormOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Prospect
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium">Total Prospects</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stats.totalProspects}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-600" />
                <span className="text-sm font-medium">Pending Apps</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stats.pendingApplications}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm font-medium">Active Affiliates</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stats.activeAffiliates}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-purple-600" />
                <span className="text-sm font-medium">Conversion Rate</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stats.conversionRate.toFixed(1)}%</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                <span className="text-sm font-medium">Monthly Payouts</span>
              </div>
              <p className="text-2xl font-bold mt-1">${stats.monthlyPayouts.toLocaleString()}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5 text-yellow-600" />
                <span className="text-sm font-medium">Total Revenue</span>
              </div>
              <p className="text-2xl font-bold mt-1">${stats.totalRevenue.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="pipeline" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="applications">
            Applications
            {stats.pendingApplications > 0 && (
              <Badge variant="destructive" className="ml-2">
                {stats.pendingApplications}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="materials">Materials</TabsTrigger>
        </TabsList>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4 items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search prospects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={filterStage} onValueChange={setFilterStage}>
              <SelectTrigger className="w-40">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Stage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                <SelectItem value="prospect">Prospect</SelectItem>
                <SelectItem value="application">Application</SelectItem>
                <SelectItem value="review">Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterSource} onValueChange={setFilterSource}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="referral">Referral</SelectItem>
                <SelectItem value="website">Website</SelectItem>
                <SelectItem value="social_media">Social Media</SelectItem>
                <SelectItem value="event">Event</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <AffiliatePipeline
            prospects={getFilteredProspects()}
            onProspectEdit={handleProspectEdit}
            onRefresh={loadAffiliateData}
          />
        </TabsContent>

        {/* Applications Tab */}
        <TabsContent value="applications">
          <AffiliateApplicationReview
            applications={getPendingApplications()}
            onApplicationUpdate={handleApplicationUpdate}
            onReview={handleApplicationReview}
          />
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance">
          <AffiliatePerformance />
        </TabsContent>

        {/* Materials Tab */}
        <TabsContent value="materials">
          <Card>
            <CardHeader>
              <CardTitle>Marketing Materials</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Marketing materials management coming soon...
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <AffiliateProspectForm
        open={prospectFormOpen}
        onClose={() => {
          setProspectFormOpen(false);
          setEditingProspect(null);
        }}
        prospect={editingProspect}
        onSave={handleProspectSave}
      />
    </div>
  );
};

export default AffiliatesPage;